import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Cascader, Dialog, Input, ZhMap, type CascaderOption, type ZhMapRef } from "zhihao-ui-react";
import type { MapViewForm } from "@zhihaoscm/entity";
import { FormTypeEnum } from "@zhihaoscm/meta";
import { areaLib } from "@/utils/areaLib";
import { CDN_URL } from "@/config";
import { onAddressInput, sliceGbCode } from "@/views/pages/logistics/wharf";

export interface ZhMapDialogProps {
    formType: FormTypeEnum;
    pointerMarker: boolean;
    isShowAddress?: boolean;
    visible?: boolean;
    data: MapViewForm;
    onUpdateVisible?: (value: boolean) => void;
    onUpdateData?: (value: MapViewForm) => void;
    onSubmit?: (data: MapViewForm, formType: FormTypeEnum) => void;
}

/** 位置选择弹窗：使用 zhihao-ui-react 的 Dialog、Cascader 和 ZhMap。 */
export default function ZhMapDialog({
    formType,
    pointerMarker,
    isShowAddress = false,
    visible = false,
    data,
    onUpdateVisible,
    onUpdateData,
    onSubmit
}: ZhMapDialogProps) {
    const mapRef = useRef<ZhMapRef | null>(null);
    const [keyword, setKeyword] = useState("");
    const [suggestions, setSuggestions] = useState<
        Array<{ value: string; link?: { lonlat?: string; countyCode?: string; address?: string } }>
    >([]);
    const options = useMemo<CascaderOption[]>(
        () => areaLib.areaList2Options(areaLib.areaData) as CascaderOption[],
        [areaLib.version]
    );
    const areaCode = data.areaCode || [];
    const title = formType === FormTypeEnum.Scan ? "查看位置" : "位置";
    const patchData = (patch: Partial<MapViewForm>) => onUpdateData?.({ ...data, ...patch });
    useEffect(() => {
        if (!visible || !data.lngLat) return;
        const [lon, lat] = data.lngLat.split(",").map(Number);
        if (Number.isFinite(lon) && Number.isFinite(lat)) mapRef.current?.setCenter(lon, lat);
    }, [visible, data.lngLat]);
    const searchAddress = async (value: string) => {
        setKeyword(value);
        if (!value.trim()) {
            setSuggestions([]);
            return;
        }
        setSuggestions(((await onAddressInput(value, areaCode[2] || "")) as typeof suggestions) || []);
    };
    const selectAddress = (item: (typeof suggestions)[number]) => {
        const lngLat = item.link?.lonlat || "";
        const nextAreaCode = item.link?.countyCode ? sliceGbCode(item.link.countyCode) : areaCode;
        patchData({
            address: item.link?.address || item.value,
            detailAddress: item.link?.address || item.value,
            lngLat,
            areaCode: nextAreaCode
        });
        const [lon, lat] = lngLat.split(",").map(Number);
        if (Number.isFinite(lon) && Number.isFinite(lat)) mapRef.current?.setCenter(lon, lat);
        setSuggestions([]);
    };
    const updateCenter = () => {
        if (isShowAddress) return;
        const center = mapRef.current?.getCenter();
        if (center) patchData({ lngLat: `${center[0].toFixed(6)},${center[1].toFixed(6)}` });
    };
    return (
        <Dialog
            title={title}
            open={visible}
            width="920px"
            closeOnClickModal={false}
            destroyOnClose
            onCancel={() => onUpdateVisible?.(false)}
            footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Button onClick={() => onUpdateVisible?.(false)}>取消</Button>
                    {formType !== FormTypeEnum.Scan && (
                        <Button type="primary" onClick={() => onSubmit?.(data, formType)}>
                            确定
                        </Button>
                    )}
                </div>
            }
        >
            {formType !== FormTypeEnum.Scan && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 10, marginBottom: 10 }}>
                    <Cascader
                        options={options}
                        value={areaCode}
                        filterable
                        placeholder="请选择地区"
                        onChange={value => patchData({ areaCode: value as string[] })}
                    />
                    <div style={{ position: "relative" }}>
                        <Input
                            value={keyword || data.detailAddress || data.address || ""}
                            maxLength={200}
                            placeholder="请输入详细地址"
                            onChange={event => searchAddress(event.target.value)}
                        />
                        {suggestions.length > 0 && (
                            <div
                                style={{
                                    position: "absolute",
                                    zIndex: 5,
                                    width: "100%",
                                    background: "#fff",
                                    border: "1px solid #ddd"
                                }}
                            >
                                {suggestions.map((item, index) => (
                                    <button
                                        key={`${item.value}-${index}`}
                                        type="button"
                                        style={{
                                            display: "block",
                                            width: "100%",
                                            padding: 8,
                                            textAlign: "left",
                                            border: 0,
                                            background: "#fff"
                                        }}
                                        onClick={() => selectAddress(item)}
                                    >
                                        {item.value} {item.link?.address}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {data.lngLat && <span style={{ lineHeight: "32px" }}>经纬度 {data.lngLat}</span>}
                </div>
            )}
            <div style={{ position: "relative", height: 482 }}>
                <ZhMap
                    ref={mapRef}
                    center={data.lngLat ? (data.lngLat.split(",").map(Number) as [number, number]) : undefined}
                    className="h-full w-full"
                    layerType="satellite"
                    copyrightImgUrl={`${CDN_URL}map/tdt-copyright-h-w.svg`}
                    mapMoveEnd={updateCenter}
                />
                {pointerMarker && !isShowAddress && (
                    <div
                        style={{
                            pointerEvents: "none",
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <img src={`${CDN_URL}map/poi-marker-default.png`} alt="pointer-marker" style={{ height: 50 }} />
                        <span style={{ padding: "1px 6px", background: "#ffffffe6", borderRadius: 4 }}>
                            {data.lngLat}
                        </span>
                    </div>
                )}
            </div>
        </Dialog>
    );
}
